import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppTheme } from "../context/ThemeContext";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

export default function UpdatePassword(){
  const navigate=useNavigate();
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();
  const {user,loadUser}=useAuth();

  const [currentPassword,setCurrentPassword]=useState("");
  const [newPassword,setNewPassword]=useState("");
  const [confirmPassword,setConfirmPassword]=useState("");
  const [saving,setSaving]=useState(false);

  const handleSubmit=async(event)=>{
    event.preventDefault();

    if(!currentPassword){
      showError("Ingresa tu clave temporal.");
      return;
    }

    if(!newPassword){
      showError("Ingresa tu nueva contraseña.");
      return;
    }

    if(newPassword!==confirmPassword){
      showError("Las nuevas contraseñas no coinciden.");
      return;
    }

    if(currentPassword===newPassword){
      showError("La nueva contraseña debe ser diferente a la clave temporal.");
      return;
    }

    try{
      setSaving(true);

      const response=await fetch("/change-password/",{
        method:"POST",
        credentials:"include",
        headers:{
          "Content-Type":"application/json",
          "X-CSRFToken":getCookie("csrftoken"),
        },
        body:JSON.stringify({
          current_password:currentPassword,
          new_password:newPassword,
          confirm_password:confirmPassword,
        }),
      });

      let data={};

      try{
        data=await response.json();
      }catch{
        throw new Error("El servidor no respondió correctamente.");
      }

      if(!response.ok){
        if(Array.isArray(data.error)){
          throw new Error(data.error.join(" "));
        }

        throw new Error(data.error||"No se pudo actualizar la contraseña.");
      }

      await loadUser();

      showSuccess("Contraseña actualizada correctamente.");

      if(user?.rol==="DELEGADO"){
        navigate("/delegado",{replace:true});
      }else{
        navigate("/admin",{replace:true});
      }
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight:"100vh",
        display:"flex",
        alignItems:"center",
        justifyContent:"center",
        backgroundColor:colors.background,
        p:2,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width:"100%",
          maxWidth:430,
          p:{xs:2.5,sm:3},
          backgroundColor:colors.surface,
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radiusLarge,
        }}
      >
        <Typography
          sx={{
            fontSize:typography.sizes.title,
            fontWeight:typography.weights.extraBold,
            color:colors.text,
          }}
        >
          Actualiza tu contraseña
        </Typography>

        <Typography
          sx={{
            mt:0.7,
            mb:2.5,
            fontSize:typography.sizes.md,
            lineHeight:1.5,
            color:colors.textSecondary,
          }}
        >
          Estás utilizando una clave temporal. Crea una nueva contraseña para continuar al sistema.
        </Typography>

        <TextField
          size="small"
          type="password"
          label="Clave temporal"
          value={currentPassword}
          onChange={(event)=>setCurrentPassword(event.target.value)}
          autoComplete="current-password"
          fullWidth
          disabled={saving}
          sx={{mb:1.5}}
        />

        <TextField
          size="small"
          type="password"
          label="Nueva contraseña"
          value={newPassword}
          onChange={(event)=>setNewPassword(event.target.value)}
          autoComplete="new-password"
          fullWidth
          disabled={saving}
          sx={{mb:1.5}}
        />

        <TextField
          size="small"
          type="password"
          label="Confirmar nueva contraseña"
          value={confirmPassword}
          onChange={(event)=>setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          fullWidth
          disabled={saving}
        />

        <Button
          type="submit"
          size="small"
          variant="contained"
          disabled={saving}
          fullWidth
          sx={{
            mt:2.5,
            minHeight:38,
            backgroundColor:colors.secondary,
            color:colors.buttonPrimaryText,
            "&:hover":{
              backgroundColor:colors.secondaryHover,
            },
          }}
        >
          {saving?(
            <CircularProgress
              size={19}
              sx={{color:colors.buttonPrimaryText}}
            />
          ):"Actualizar contraseña"}
        </Button>
      </Box>
    </Box>
  );
}